import React, { useEffect, useState, useCallback } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import CustomPagination from "components/common/CustomPagination"
import { useTranslation } from "react-i18next"
import EquipmentTable from "./equipmentTable"
import EquipmentCardList from "./components/EquipmentCardList"
import FilterChips from "ui/FilterChips"
import EquipmentFilters from "./components/EquipmentFilters"
import EquipmentModals from "./components/EquipmentModals"
import { useEquipmentData } from "./hooks/useEquipmentData"
import { useEquipmentFilters } from "./hooks/useEquipmentFilters"
import { useEquipmentModals } from "./hooks/useEquipmentModals"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import {
  deactivateEquipment,
  activateEquipment,
  deleteEquipment,
  getGuideBoatsListing,
  getEquipmentDeactivationDetails,
  getEquipmentDeletionDetails,
  selectDeactivateEquipmentLoading,
  selectDeleteEquipmentLoading,
  selectEquipmentDeactivationDetails,
  selectEquipmentDeactivationDetailsLoading,
  selectEquipmentDeletionDetails,
  selectEquipmentDeletionDetailsLoading,
  updateBoat
} from "store/boats"
import { Flex } from "antd"
import { routes } from "router/routes"
import { determineDeactivateMode } from "./utils/determineDeactivateMode"
import { toaster } from "components/common/Toast"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import AssignExperiencesModal from "ui/AssignExperiencesModal"

export default function EquipmentPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()

  // Responsive breakpoint detection
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsTabletOrMobile(window.innerWidth <= 1024)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])

  // Redux selectors
  const deactivateLoading = useSelector(selectDeactivateEquipmentLoading)
  const deleteLoading = useSelector(selectDeleteEquipmentLoading)
  const equipmentDeactivationDetails = useSelector(selectEquipmentDeactivationDetails)
  const deactivationDetailsLoading = useSelector(selectEquipmentDeactivationDetailsLoading)
  const equipmentDeletionDetails = useSelector(selectEquipmentDeletionDetails)
  const deletionDetailsLoading = useSelector(selectEquipmentDeletionDetailsLoading)

  // Custom hooks for data and filters
  const { equipmentData, boatsLoading, categoryListing } = useEquipmentData()
  const {
    search,
    selectedCategories,
    currentPage,
    pageSize,
    categoryOptions,
    filteredData,
    paginatedData,
    setSearch,
    setSelectedCategories,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange
  } = useEquipmentFilters(equipmentData, categoryListing, t)

  // Custom hook for modal state management
  const {
    categoryModalOpen,
    openCategoryModal,
    closeCategoryModal,
    deactivateDeleteModalState,
    openDeactivateModal,
    openDeleteModal,
    closeDeactivateDeleteModal,
    setDeactivateDeleteDetails
  } = useEquipmentModals()

  // State for assign experiences modal
  const [assignModalState, setAssignModalState] = useState({
    visible: false,
    equipment: null
  })

  // Get trips from Redux store for modal
  const trips = useSelector((state) => state.trips.items || [])

  /**
   * Fetch equipment deactivation details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeactivationDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "deactivate") {
      // Determine mode from API response or compute it
      const mode = equipmentDeactivationDetails.mode || determineDeactivateMode(equipmentDeactivationDetails)

      setDeactivateDeleteDetails({
        ...equipmentDeactivationDetails,
        mode
      })
    }
  }, [equipmentDeactivationDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  /**
   * Fetch equipment deletion details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeletionDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "delete") {
      // Determine mode from API response or compute it
      const mode = equipmentDeletionDetails.mode || determineDeactivateMode(equipmentDeletionDetails)

      setDeactivateDeleteDetails({
        ...equipmentDeletionDetails,
        mode
      })
    }
  }, [equipmentDeletionDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  /**
   * Handle deactivate button click from table
   * Fetches equipment details and opens modal
   */
  const handleDeactivateClick = (equipment) => {
    const equipmentId = equipment?.key || equipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    // Open modal immediately (will show loading state)
    openDeactivateModal(equipment)

    // Fetch deactivation details from API
    dispatch(getEquipmentDeactivationDetails(equipmentId)).catch((error) => {
      console.error("Failed to fetch equipment details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle delete button click from table
   * Fetches equipment details and opens modal
   */
  const handleDeleteClick = (equipment) => {
    const equipmentId = equipment?.key || equipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    // Open modal immediately (will show loading state)
    openDeleteModal(equipment)

    // Fetch deletion details from API
    dispatch(getEquipmentDeletionDetails(equipmentId)).catch((error) => {
      console.error("Failed to fetch equipment deletion details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle activate button click from table
   * Activates the equipment directly without modal
   */
  const handleActivateClick = (equipment) => {
    const equipmentId = equipment?.key || equipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    dispatch(activateEquipment(equipmentId))
      .then((response) => {
        // Use message from backend response if available
        const successMessage = response?.message || `${equipment.title} has been activated successfully`
        toaster.success(successMessage)
        // Ensure list reflects latest status
        dispatch(getGuideBoatsListing())
      })
      .catch((error) => {
        console.error("Equipment activation failed:", error)
      })
  }

  /**
   * Handle assign experiences click from table
   * Opens modal with current equipment data
   */
  const handleAssignClick = useCallback((equipment) => {
    setAssignModalState({
      visible: true,
      equipment
    })
  }, [])

  /**
   * Handle assign experiences modal save
   * Updates equipment with new experience assignments
   */
  const handleAssignSave = async (selectedExperienceIds) => {
    const { equipment } = assignModalState
    const equipmentId = equipment?.key || equipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      throw new Error("No equipment selected")
    }

    try {
      const formData = new FormData()
      for (const tripId of selectedExperienceIds) {
        formData.append("boat[trip_ids][]", tripId)
      }
      if (selectedExperienceIds.length === 0) {
        formData.append("boat[trip_ids][]", "")
      }
      await dispatch(updateBoat(equipmentId, formData))
      // Refresh equipment listing to get updated assigned_experiences count
      dispatch(getGuideBoatsListing())
      setAssignModalState({ visible: false, equipment: null })
    } catch (error) {
      console.error("Failed to assign experiences:", error)
      throw error
    }
  }

  /**
   * Handle assign experiences modal cancel
   */
  const handleAssignCancel = () => {
    setAssignModalState({ visible: false, equipment: null })
  }

  /**
   * Handle deactivate/delete modal confirmation
   * Dispatches appropriate Redux action based on action type and mode
   * @param {object} data - Additional data for the request (mode, replacement_ids, etc.)
   */
  const handleDeactivateDeleteOk = async (data = {}) => {
    const { equipment, action } = deactivateDeleteModalState

    // Equipment data uses 'key' field for ID (mapped from item.id in useEquipmentData)
    const equipmentId = equipment?.key || equipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const actionFn = action === "deactivate" ? deactivateEquipment : deleteEquipment

    dispatch(actionFn(equipmentId, data))
      .then(() => {
        toaster.success(
          action === "deactivate"
            ? t("equipment.success.deactivated", { name: equipment.title })
            : t("equipment.success.deleted", { name: equipment.title })
        )
        closeDeactivateDeleteModal()
        // Refresh listing to reflect changes
        dispatch(getGuideBoatsListing())
      })
      .catch((error) => {
        console.error("Equipment operation failed:", error)
      })
  }

  // Combine details loading states based on action type
  const detailsLoading = deactivateDeleteModalState.action === "deactivate" ? deactivationDetailsLoading : deletionDetailsLoading

  return (
    <div className="vstack flex-fill z-0 mt-l" id="clients">
      <FirstLoading loading={boatsLoading || detailsLoading}>
        <Flex vertical gap={24}>
          <div>
            <EquipmentFilters
              search={search}
              onSearchChange={setSearch}
              selectedCategories={selectedCategories}
              onCategoryChange={setSelectedCategories}
              categoryOptions={categoryOptions}
              onManageCategoriesClick={openCategoryModal}
              onAddEquipmentClick={() => navigate(routes.guideEquipmentAddPath())}
            />
            <FilterChips selectedValues={selectedCategories} options={categoryOptions} onChange={setSelectedCategories} className="mt-s" />
          </div>
          <WithEmptyStateWrapper
            name="equipment"
            hasItems={paginatedData?.length > 0}
            fallbackTitle={t("equipment.no_equipment", { defaultValue: "No equipment yet" })}
            fallbackButtonText={hasPermission(MODULES.EQUIPMENT, ACTIONS.CREATE) ? t("equipment.add_equipment") : undefined}
            fallbackButtonClickHandler={hasPermission(MODULES.EQUIPMENT, ACTIONS.CREATE) ? () => navigate(routes.guideEquipmentAddPath()) : undefined}
            fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
          >
            {isTabletOrMobile ? (
              <EquipmentCardList
                data={paginatedData}
                onDeactivate={handleDeactivateClick}
                onActivate={handleActivateClick}
                onDelete={handleDeleteClick}
                onAssignClick={handleAssignClick}
              />
            ) : (
              <EquipmentTable
                data={paginatedData}
                onChange={handleSortChange}
                onDeactivate={handleDeactivateClick}
                onActivate={handleActivateClick}
                onDelete={handleDeleteClick}
                onAssignClick={handleAssignClick}
              />
            )}
          </WithEmptyStateWrapper>

          {filteredData?.length > 0 && (
            <CustomPagination
              metadata={{
                page: currentPage,
                total_pages: Math.ceil(filteredData.length / pageSize),
                next_page: currentPage < Math.ceil(filteredData.length / pageSize) ? currentPage + 1 : null,
                prev_page: currentPage > 1 ? currentPage - 1 : null
              }}
              pageSize={pageSize}
              total={filteredData.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          )}
        </Flex>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
        <EquipmentModals
          categoryModalOpen={categoryModalOpen}
          onCategoryModalClose={closeCategoryModal}
          deactivateDeleteModalState={deactivateDeleteModalState}
          onDeactivateDeleteModalClose={closeDeactivateDeleteModal}
          onDeactivateDeleteModalOk={handleDeactivateDeleteOk}
          loading={deactivateDeleteModalState.action === "deactivate" ? deactivateLoading : deleteLoading}
          detailsLoading={detailsLoading}
        />
        <AssignExperiencesModal
          open={assignModalState.visible}
          onCancel={handleAssignCancel}
          onSave={handleAssignSave}
          currentExperiences={
            assignModalState.equipment?.assigned_experiences
              ? trips.filter((trip) => assignModalState.equipment.assigned_experiences.includes(trip.id))
              : []
          }
        />
      </FirstLoading>
    </div>
  )
}
